/** Longest-prefix wins. Paths are hash-router paths without `#` or query. */
export type FeatureRouteEntry = {pathPrefix: string; feature: string};

/** Routes that map to `platform` despite having a route.key */
const PLATFORM_ROUTE_KEYS = new Set([
  "dashboard",
  "home",
  "help-debug",
  "rbac-admin",
  "company",
  "emergency-contacts",
  "support",
  "view-as-user",
]);

/** Explicit overrides where feature !== route.key */
const FEATURE_OVERRIDES: Record<string, string> = {
  "shifts-history": "shifts",
  "company-vehicles": "companyvehicles",
};

export const FEATURE_ROUTE_REGISTRY: FeatureRouteEntry[] = [
  {pathPrefix: "/resident-claim/verify", feature: "platform"},
  {pathPrefix: "/resident-claim", feature: "platform"},
  {pathPrefix: "/acknowledge-shift", feature: "shifts"},
  {pathPrefix: "/notifications", feature: "briefings"},
  {pathPrefix: "/vehicle-maintenance", feature: "vehiclemaintenances"},
  {pathPrefix: "/location-denied", feature: "platform"},
  {pathPrefix: "/logout", feature: "platform"},
  {pathPrefix: "/property-inspection-reports", feature: "propertyinspectionreport"},
  {pathPrefix: "/parking-enforcement", feature: "vehicles"},
  {pathPrefix: "/permanent-parking/new", feature: "permanentparking"},
  {pathPrefix: "/permanent-parking", feature: "permanentparking"},
  {pathPrefix: "/stored-vehicles", feature: "storedvehicles"},
  {pathPrefix: "/company-vehicles", feature: "companyvehicles"},
  {pathPrefix: "/tennisaccesslogs", feature: "tennisaccesslogs"},
  {pathPrefix: "/recreationaccesslogs", feature: "recreationaccesslogs"},
  {pathPrefix: "/gymaccesslogs", feature: "gymaccesslogs"},
  {pathPrefix: "/poolaccesslogs", feature: "poolaccesslogs"},
  {pathPrefix: "/dispatchlogs", feature: "dispatchlogs"},
  {pathPrefix: "/reportrequests", feature: "reportrequests"},
  {pathPrefix: "/safelistings", feature: "safelistings"},
  {pathPrefix: "/shifts-history", feature: "shifts"},
  {pathPrefix: "/briefings", feature: "briefings"},
  {pathPrefix: "/households", feature: "households"},
  {pathPrefix: "/incidents", feature: "incidents"},
  {pathPrefix: "/citations", feature: "citations"},
  {pathPrefix: "/properties", feature: "properties"},
  {pathPrefix: "/dashboard", feature: "platform"},
  {pathPrefix: "/emergency-contacts", feature: "platform"},
  {pathPrefix: "/rbac-admin", feature: "platform"},
  {pathPrefix: "/recreation", feature: "recreation"},
  {pathPrefix: "/support/view-as-user", feature: "platform"},
  {pathPrefix: "/help/debug", feature: "platform"},
  {pathPrefix: "/company", feature: "platform"},
  {pathPrefix: "/support", feature: "platform"},
  {pathPrefix: "/tennis", feature: "tennis"},
  {pathPrefix: "/shifts", feature: "shifts"},
  {pathPrefix: "/users", feature: "users"},
  {pathPrefix: "/tasks", feature: "tasks"},
  {pathPrefix: "/pools", feature: "pools"},
  {pathPrefix: "/dars", feature: "dars"},
  {pathPrefix: "/dispatchdesk", feature: "platform"},
  {pathPrefix: "/home", feature: "platform"},
  {pathPrefix: "/gym", feature: "gym"},
];

export const FEATURE_ROUTE_REGISTRY_SORTED = [...FEATURE_ROUTE_REGISTRY].sort(
  (a, b) => b.pathPrefix.length - a.pathPrefix.length,
);

export const API_PREFIX_FEATURE_MAP: Array<{prefix: string; feature: string}> = [
  {prefix: "shift-template", feature: "shifts"},
  {prefix: "shift-coverage", feature: "shifts"},
  {prefix: "shift/acknowledge", feature: "shifts"},
  {prefix: "shift/suggest-breaks", feature: "shifts"},
  {prefix: "shift/", feature: "shifts"},
  {prefix: "emergency-shift", feature: "shifts"},
  {prefix: "break", feature: "shifts"},
  {prefix: "property-inspection", feature: "propertyinspectionreport"},
  {prefix: "recreationaccesslog", feature: "recreationaccesslogs"},
  {prefix: "tennisaccesslog", feature: "tennisaccesslogs"},
  {prefix: "gymaccesslog", feature: "gymaccesslogs"},
  {prefix: "poolaccesslog", feature: "poolaccesslogs"},
  {prefix: "dispatchlog", feature: "dispatchlogs"},
  {prefix: "briefingboard", feature: "briefings"},
  {prefix: "reportrequest", feature: "reportrequests"},
  {prefix: "safelisting", feature: "safelistings"},
  {prefix: "stored-vehicle", feature: "storedvehicles"},
  {prefix: "storedvehicle", feature: "storedvehicles"},
  {prefix: "permanent-parking", feature: "permanentparking"},
  {prefix: "permanentparking", feature: "permanentparking"},
  {prefix: "company-vehicle", feature: "companyvehicles"},
  {prefix: "citation", feature: "citations"},
  {prefix: "incident", feature: "incidents"},
  {prefix: "household", feature: "households"},
  {prefix: "warning", feature: "warnings"},
  {prefix: "property", feature: "properties"},
  {prefix: "dar", feature: "dars"},
  {prefix: "vehicle", feature: "vehicles"},
  {prefix: "user", feature: "users"},
  {prefix: "task", feature: "tasks"},
  {prefix: "login", feature: "platform"},
  {prefix: "notification", feature: "platform"},
  {prefix: "me", feature: "platform"},
];

export const API_PREFIX_FEATURE_MAP_SORTED = [...API_PREFIX_FEATURE_MAP].sort(
  (a, b) => b.prefix.length - a.prefix.length,
);

export function routeKeyToFeature(routeKey: string): string {
  if (PLATFORM_ROUTE_KEYS.has(routeKey)) return "platform";
  return FEATURE_OVERRIDES[routeKey] ?? routeKey;
}

export const PLATFORM_FEATURE = "platform";
